// VenueVerify.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Public owner verification page. No auth required.
// Route: /verify-venue?token=...
// Flow: GET /api/onboarding/verify/:token → show terms → OTP → confirmed
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, Button, TextField,
  Checkbox, FormControlLabel, Alert, Divider, Paper,
} from '@mui/material';
import { C } from '../theme';

const API = process.env.REACT_APP_API_URL || 'https://api.binko.me';

const sx = {
  root: {
    minHeight: '100vh',
    bgcolor: C.bgApp,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    p: 2,
    pt: 4,
  },
  card: {
    bgcolor: C.bgCard,
    borderRadius: 2,
    p: 3,
    width: '100%',
    maxWidth: 520,
    border: `1px solid ${C.border}`,
  },
  label: { color: C.textMuted, fontSize: '0.78rem', mb: 0.3 },
  value: { color: C.textPrimary, mb: 1.5 },
  termRow: {
    display: 'flex',
    gap: 1.5,
    mb: 1.5,
    alignItems: 'flex-start',
  },
  termNum: {
    color: C.yellow,
    fontWeight: 700,
    minWidth: 20,
    fontSize: '0.85rem',
    pt: 0.1,
  },
  termText: { color: C.textPrimary, fontSize: '0.88rem', lineHeight: 1.55 },
};

export default function VenueVerify() {
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [stage, setStage]       = useState('loading'); // loading | error | terms | otp | done
  const [errorMsg, setErrorMsg] = useState('');
  const [info, setInfo]         = useState(null);       // { venue_name, owner_name, terms }
  const [agreed, setAgreed]     = useState(false);
  const [otpSent, setOtpSent]   = useState(false);
  const [otp, setOtp]           = useState('');
  const [busy, setBusy]         = useState(false);
  const [fieldErr, setFieldErr] = useState('');

  // ── On mount: validate token ──────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setErrorMsg('No verification token found in this link.'); setStage('error'); return; }
    fetch(`${API}/api/onboarding/verify/${token}`)
      .then(r => {
        if (r.status === 404) throw new Error('This verification link is invalid.');
        if (r.status === 410) throw new Error('This verification link has expired or has already been used.');
        if (!r.ok) throw new Error('Something went wrong. Please contact Binko support.');
        return r.json();
      })
      .then(data => {
        setInfo(data.application);
        setStage('terms');
      })
      .catch(err => { setErrorMsg(err.message); setStage('error'); });
  }, [token]);

  // ── Send OTP ──────────────────────────────────────────────────────────────
  async function handleSendOtp() {
    if (!agreed) { setFieldErr('Please accept all terms before continuing.'); return; }
    setFieldErr('');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/onboarding/verify/${token}/send-otp`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to send OTP.');
      setOtpSent(true);
      setStage('otp');
    } catch (err) {
      setFieldErr(err.message);
    } finally {
      setBusy(false);
    }
  }

  // ── Confirm OTP ───────────────────────────────────────────────────────────
  async function handleConfirm() {
    if (otp.length !== 6) { setFieldErr('Enter the 6-digit OTP.'); return; }
    setFieldErr('');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/onboarding/verify/${token}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp, accepted_terms: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Verification failed.');
      setStage('done');
    } catch (err) {
      setFieldErr(err.message);
    } finally {
      setBusy(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={sx.root}>
      <Paper elevation={0} sx={sx.card}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            bgcolor: C.yellow, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '1.1rem', color: C.yellowText }}>B</Typography>
          </Box>
          <Typography sx={{ color: C.textPrimary, fontWeight: 700, fontSize: '1.1rem' }}>
            Binko — Venue Verification
          </Typography>
        </Box>

        {/* Loading */}
        {stage === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ color: C.teal }} size={32} />
            <Typography sx={{ color: C.textMuted, mt: 2, fontSize: '0.9rem' }}>
              Checking your link…
            </Typography>
          </Box>
        )}

        {/* Error */}
        {stage === 'error' && (
          <Alert severity="error" sx={{ bgcolor: '#2a1010', color: '#ff8a8a', border: '1px solid #5a1a1a' }}>
            {errorMsg}
          </Alert>
        )}

        {/* Terms stage */}
        {stage === 'terms' && info && (
          <>
            <Typography sx={{ color: C.textMuted, fontSize: '0.78rem', mb: 0.3 }}>Venue</Typography>
            <Typography sx={{ color: C.textPrimary, fontWeight: 600, fontSize: '1.05rem', mb: 0.5 }}>
              {info.venue_name}
            </Typography>
            {info.owner_name && (
              <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', mb: 2 }}>
                Hi {info.owner_name} — please review and accept the following before verifying.
              </Typography>
            )}

            <Divider sx={{ borderColor: C.border, mb: 2 }} />

            <Typography sx={{ color: C.yellow, fontWeight: 700, fontSize: '0.8rem', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Terms of Enrolment
            </Typography>

            {(info.terms_points || []).map((point, i) => (
              <Box key={i} sx={sx.termRow}>
                <Typography sx={sx.termNum}>{i + 1}.</Typography>
                <Typography sx={sx.termText}>{(i === 4 && info.terms_url) ? <>{point.slice(0, point.indexOf('Binko venue'))}<a href={info.terms_url} target="_blank" rel="noopener noreferrer" style={{ color: '#4dd0c4', textDecoration: 'underline' }}>Binko venue terms of service and privacy policy</a>{point.slice(point.indexOf('Binko venue') + 'Binko venue terms of service and privacy policy'.length)}</> : point}</Typography>
              </Box>
            ))}

            {info.terms_version && (
              <Typography sx={{ color: C.textDisabled, fontSize: '0.72rem', mt: 1, mb: 2 }}>
                Terms version: {info.terms_version}
              </Typography>
            )}

            <Divider sx={{ borderColor: C.border, mb: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  sx={{ color: C.textMuted, '&.Mui-checked': { color: C.teal } }}
                />
              }
              label={
                <Typography sx={{ color: C.textPrimary, fontSize: '0.88rem' }}>
                  I have read and agree to all the above terms on behalf of the venue.
                </Typography>
              }
            />

            {fieldErr && (
              <Alert severity="warning" sx={{ mt: 1.5, bgcolor: '#2a1f00', color: '#ffd080', border: '1px solid #5a4000', fontSize: '0.85rem' }}>
                {fieldErr}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              disabled={busy || !agreed}
              onClick={handleSendOtp}
              sx={{ mt: 2, bgcolor: C.teal, color: C.bgApp, fontWeight: 700, '&:hover': { bgcolor: C.tealFill } }}
            >
              {busy ? <CircularProgress size={20} sx={{ color: C.bgApp }} /> : 'Send OTP to my mobile'}
            </Button>
          </>
        )}

        {/* OTP stage */}
        {stage === 'otp' && (
          <>
            <Typography sx={{ color: C.textPrimary, fontWeight: 600, mb: 1 }}>
              Enter the OTP sent to your mobile
            </Typography>
            <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', mb: 2.5 }}>
              A 6-digit code has been sent to the registered owner mobile number.
            </Typography>

            <TextField
              label="6-digit OTP"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              fullWidth
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { bgcolor: C.bgElevated, color: C.textPrimary },
                '& .MuiInputLabel-root': { color: C.textMuted },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
              }}
            />

            {fieldErr && (
              <Alert severity="error" sx={{ mb: 2, bgcolor: '#2a1010', color: '#ff8a8a', border: '1px solid #5a1a1a', fontSize: '0.85rem' }}>
                {fieldErr}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              disabled={busy || otp.length !== 6}
              onClick={handleConfirm}
              sx={{ bgcolor: C.teal, color: C.bgApp, fontWeight: 700, '&:hover': { bgcolor: C.tealFill } }}
            >
              {busy ? <CircularProgress size={20} sx={{ color: C.bgApp }} /> : 'Confirm & Verify'}
            </Button>

            <Button
              variant="text"
              fullWidth
              disabled={busy}
              onClick={() => { setStage('terms'); setOtp(''); setFieldErr(''); }}
              sx={{ mt: 1, color: C.textMuted, fontSize: '0.8rem' }}
            >
              ← Back
            </Button>
          </>
        )}

        {/* Done */}
        {stage === 'done' && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>🎉</Typography>
            <Typography sx={{ color: C.success, fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>
              Verification complete!
            </Typography>
            <Typography sx={{ color: C.textMuted, fontSize: '0.88rem' }}>
              Your venue has been verified. The Binko team will be in touch shortly to complete the setup.
            </Typography>
          </Box>
        )}

      </Paper>
    </Box>
  );
}
